import { useConfirmation } from "src/sudokit/components/ConfirmationDialog/ConfirmationProvider";
import { useMemo, useState } from "react";
import {
  isTrialDiscussModeBlocked,
  isTrialFreeChatBlocked,
  subscriptionViewFromClientJSON,
} from "~shared/trialFreeChatAccess";
import Auth from "../../Auth";
import { SudoButton } from "../../sudokit/components/SudoButton";
import { SudoTooltip } from "../../sudokit/components/SudoTooltip";

type TrialFreeChatTestingProps = {
  userId?: string;
  subscription?: {
    activeTrial?: () => boolean;
    data?: {
      signedUpAt?: Date | { toDate?: () => Date; _seconds?: number } | string | null;
      quota?: number;
    };
  } | null;
  toolsEnabled: boolean;
  setToast: (content: string) => void;
  onUpdated: () => void;
};

function formatSignedUpAt(value: unknown): string {
  if (!value) return "Unknown";
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === "object" && value !== null && "toDate" in value && typeof value.toDate === "function") {
    return value.toDate().toLocaleString();
  }
  if (typeof value === "object" && value !== null && "_seconds" in value && typeof value._seconds === "number") {
    return new Date(value._seconds * 1000).toLocaleString();
  }
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
}

export function TrialFreeChatTesting({
  userId,
  subscription,
  toolsEnabled,
  setToast,
  onUpdated,
}: TrialFreeChatTestingProps) {
  const confirmAction = useConfirmation(userId);
  const [loadingAction, setLoadingAction] = useState<"expire" | "reset" | null>(null);

  const subscriptionView = useMemo(
    () =>
      subscriptionViewFromClientJSON({
        activeTrial: subscription?.activeTrial?.() ?? false,
        data: {
          quota: subscription?.data?.quota,
          signedUpAt: subscription?.data?.signedUpAt ?? null,
        },
      }),
    [subscription],
  );

  const isTrialUser = subscription?.activeTrial?.() ?? false;
  const isDiscussBlocked = isTrialDiscussModeBlocked(subscriptionView);
  const isBlocked = isTrialFreeChatBlocked(subscriptionView);

  const statusLabel = !isTrialUser
    ? "Not on trial"
    : isBlocked
      ? "Blocked — past free-chat week with zero credits"
      : isDiscussBlocked
        ? "Past free-chat week — only Allow edits works with trial credits"
        : "Within free-chat week — Discuss is free";

  const runSimulation = async (action: "expire" | "reset") => {
    if (!userId) return;
    const confirmMessage = action === "expire" ? "Backdate this user's signedUpAt so agentic chat treats them as past the 1-week free chat window?" : "Reset this user's signedUpAt to now so they are back inside the 1-week free chat window?";
    await confirmAction({
      title: "Change free chat trial",
      message: confirmMessage,
      actionLabel: "Confirm",
      destructive: false
    }, async () => {
      setLoadingAction(action);
      try {
        const result = await Auth.fetchWithIdToken(`/api/admin/user/${userId}/trial-free-chat-simulation`, {
          action
        });
        setToast(action === "expire" ? "Trial free-chat week expired for testing" : "Trial free-chat week reset for testing");
        if (result?.signedUpAt) {
          setToast(`signedUpAt is now ${new Date(result.signedUpAt).toLocaleString()}`);
        }
        onUpdated();
      } catch (error: any) {
        setToast(error?.message || "Failed to update trial free-chat simulation");
      } finally {
        setLoadingAction(null);
      }
    });
  };

  if (!subscription || !isTrialUser) {
    return null;
  }

  return (
    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
      <h4 className="text-base font-semibold text-blue-900 mb-2">Trial Free Chat Testing</h4>
      <p className="text-sm text-blue-700 mb-3">
        Agentic chat gives trial users one free chat week from <code className="text-xs">signedUpAt</code>. Use these controls to simulate before/after that week without waiting.
      </p>
      <dl className="grid grid-cols-1 gap-2 text-sm text-blue-900 mb-4">
        <div>
          <dt className="font-medium">signedUpAt</dt>
          <dd>{formatSignedUpAt(subscription.data?.signedUpAt)}</dd>
        </div>
        <div>
          <dt className="font-medium">Current status</dt>
          <dd>{statusLabel}</dd>
        </div>
        <div>
          <dt className="font-medium">Quota</dt>
          <dd>{subscription.data?.quota ?? 0} — set quota to 0 above to test the full subscribe block after expiry.</dd>
        </div>
      </dl>
      <div className="flex flex-wrap gap-3">
        <SudoTooltip text="Backdates signedUpAt by 8 days so Discuss ends. Trial users can keep chatting in Allow edits if they still have credits. Combine with quota = 0 to test the full subscribe block.">
          <SudoButton
            variant="secondary"
            size="default"
            disabled={!toolsEnabled || loadingAction !== null}
            state={loadingAction === "expire" ? "loading" : "default"}
            onClick={(event) => {
              event.preventDefault();
              void runSimulation("expire");
            }}
          >
            Expire free chat week
          </SudoButton>
        </SudoTooltip>
        <SudoTooltip text="Sets signedUpAt to now so the user is back inside the complimentary chat week.">
          <SudoButton
            variant="tertiary"
            size="default"
            disabled={!toolsEnabled || loadingAction !== null}
            state={loadingAction === "reset" ? "loading" : "default"}
            onClick={(event) => {
              event.preventDefault();
              void runSimulation("reset");
            }}
          >
            Reset free chat week
          </SudoButton>
        </SudoTooltip>
      </div>
    </div>
  );
}
